import React, { useState, useEffect } from "react";
import { Paperclip } from "lucide-react";
import { Task } from "../../types/types";

// Define interface for organization user
interface OrganizationUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  role: string;
  createdAt: string | null;
  emailVerified: boolean;
  teamIds: string[];
  organizationId: string | null;
}

const priorityColors = {
  low: "bg-green-200 text-green-800",
  medium: "bg-yellow-200 text-yellow-800",
  high: "bg-red-200 text-red-800",
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case "story":
      return "📖";
    case "bug":
      return "🐛";
    case "task":
      return "✅";
    default:
      return "📋";
  }
};

interface CardProps {
  task: Task;
  onClick?: (task: Task) => void;
}

const Card: React.FC<CardProps> = ({ task, onClick }) => {
  const [organizationUsers, setOrganizationUsers] = useState<
    OrganizationUser[]
  >([]);

  // Load organization data from localStorage
  useEffect(() => {
    try {
      const organizationDataString = localStorage.getItem("organizationData");
      if (organizationDataString) {
        const organizationData = JSON.parse(organizationDataString);
        if (organizationData && Array.isArray(organizationData.users)) {
          setOrganizationUsers(organizationData.users);
        }
      }
    } catch (err) {
      console.error("Error loading organization data:", err);
    }
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick(task);
    }
  };

  return (
    <div
      className="p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer card hover:bg-gray-100 hover:border-gray-300 transition-colors"
      onClick={handleClick}
    >
      <div className="flex items-start space-x-2 mb-2">
        <span className="text-sm flex-shrink-0">
          {getTypeIcon(task.type || "story")}
        </span>
        <span className="font-semibold text-gray-800 text-sm">
          {task.title}
        </span>
      </div>

      <div className="flex items-center space-x-1 mb-2">
        {task.assigneeUserId ? (
          <div className="w-5 h-5 bg-blue-500 text-white flex items-center justify-center rounded-full text-xs font-bold">
            {(() => {
              const user = organizationUsers.find(
                (u) => u.id.toString() === task.assigneeUserId?.toString()
              );
              return user
                ? `${user.firstName[0]}${user.lastName[0]}`
                : task.assigneeInitials || "?";
            })()}
          </div>
        ) : (
          <div className="w-5 h-5 bg-gray-400 text-white flex items-center justify-center rounded-full text-xs font-bold">
            ?
          </div>
        )}
        <span className="text-xs text-gray-600">
          {(() => {
            if (task.assigneeUserId) {
              const user = organizationUsers.find(
                (u) => u.id.toString() === task.assigneeUserId?.toString()
              );
              return user
                ? `${user.firstName} ${user.lastName}`
                : task.assignee || "Unassigned";
            }
            return task.assignee || "Unassigned";
          })()}
        </span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <a
            href={`/tasks/${task.id}`}
            onClick={(e) => {
              e.stopPropagation();
              // allow default navigation via anchor
            }}
            className="text-xs text-blue-600 hover:underline"
            title={`Open task #${task.id}`}
          >
            #{task.id}
          </a>
          {task.storyPoints && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
              {task.storyPoints} pts
            </span>
          )}
          {((task.attachments && task.attachments.length > 0) ||
            (task.attachmentUrls && task.attachmentUrls.length > 0)) && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
              <Paperclip className="w-3 h-3" />
              <span className="text-xs font-medium">
                {(task.attachments?.length || 0) +
                  (task.attachmentUrls?.length || 0)}
              </span>
            </div>
          )}
        </div>
        <span
          className={`${
            priorityColors[task.priority]
          } text-xs font-semibold px-2 py-1 rounded-full`}
        >
          {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
        </span>
      </div>
    </div>
  );
};

export default Card;
